import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Autocomplete, Chip, Avatar, TextField } from "@mui/material";

import { ReactComponent as ChevronUp } from "@assets/icons/inbox/chevron-up.svg";

import { ReactComponent as ChevronDown } from "@assets/icons/inbox/chevron-down.svg";
import { useSearch } from "@/utils";

import { SvgIcon } from "@/components/Icon/SvgIcon";

import { useLazyGetPersonSuggestionQuery } from "@/pages/Private/redux/liPeople/liPeople.api";

export interface LinkedinParams {
	name: string;
	id: string;
	image?: string;
	subtitle?: string;
}

interface LinkedinPersonTagAutocompleteProps {
	value?: LinkedinParams;
	labelText?: string;
	type: string;
	handleValueChange: (value: LinkedinParams | null) => void;
	placeholder?: string;
	multiple?: boolean;
}

export const LinkedinPersonTagAutocomplete: FunctionComponent<
	LinkedinPersonTagAutocompleteProps
> = ({ value, type, handleValueChange, placeholder, labelText }) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`liPeople.${key}`);

	const [organizationSuggestions, setOrganizationSuggestions] = useState<LinkedinParams[]>();
	const { query: companyQuery, setSearchValue, searchValue } = useSearch();

	const [defaults, setDefaults] = useState<LinkedinParams>();
	const [showDropdown, setShowDropdown] = useState<boolean>(false);
	const [fetchPeople, { isFetching }] = useLazyGetPersonSuggestionQuery();

	useEffect(() => {
		setDefaults(value);
	}, [value]);

	useEffect(() => {
		if (companyQuery) {
			fetchPeople({ keyword: companyQuery }).then((results) => {
				const optionValues = results?.data?.suggestions?.map((suggestion) => ({
					name: suggestion.title,
					id: suggestion.urn,
					image: suggestion.image,
					subtitle: suggestion.subtitle,
				}));

				if (optionValues) {
					setOrganizationSuggestions([...optionValues]);
				}
			});
		}
	}, [fetchPeople, companyQuery]);

	return (value && defaults) || !value ? (
		<div className="flex flex-col w-full">
			<div className="text-sm font-medium text-gray-900 mb-2">
				{labelText || ts(`${type}.label`)}
			</div>
			<Autocomplete
				defaultValue={value ? [value] : []}
				filterOptions={(options) => {
					return options;
				}}
				getOptionLabel={(option) => option?.name || ""}
				id={type}
				inputValue={searchValue}
				loading={isFetching}
				loadingText={"Searching..."}
				multiple={true}
				noOptionsText={searchValue.length > 0 ? "Typing..." : "Start typing to search"}
				options={
					organizationSuggestions?.map((suggestion) => ({
						name: suggestion.name,
						id: suggestion.id,
						image: suggestion.image,
						subtitle: suggestion.subtitle,
					})) || []
				}
				popupIcon={
					<SvgIcon className="w-[20px] h-[20px]" svgIcon={showDropdown ? ChevronUp : ChevronDown} />
				}
				renderInput={(params) => {
					return (
						<TextField
							sx={{
								width: "100%",
								minHeight: 48,
								marginBottom: 1,
								"& .MuiFilledInput-root": {
									backgroundColor: "white",
									height: 48,
									marginBottom: 0,
									borderRadius: 1.5,
									paddingTop: "0px",
									paddingBottom: "0px !important",
									paddingLeft: 1.5,
									"&.Mui-focused": {
										borderWidth: 1,
										boxShadow: "none",
									},
								},
							}}
							{...params}
							placeholder={placeholder}
						/>
					);
				}}
				renderOption={(props, option) => (
					<li {...props}>
						<img alt={option?.name} className="w-4 h-4 mr-2" src={option?.image} />
						<span>{option?.name}</span>
					</li>
				)}
				renderTags={(value, getTagProps) =>
					value.map((option, index) => (
						<Chip
							label={option?.name || ""}
							{...getTagProps({ index })}
							key={`${option?.id}`}
							avatar={option?.image ? <Avatar alt={option?.name} src={option?.image} /> : undefined}
						/>
					))
				}
				size="medium"
				sx={{
					width: "100%",
				}}
				value={defaults ? [defaults] : []}
				onChange={(event, value) => {
					handleValueChange(value?.[0] || null);
				}}
				onClose={() => {
					setShowDropdown(false);
				}}
				onInputChange={(event, newInputValue) => {
					if (event?.type === "change") {
						setOrganizationSuggestions([]);
						setSearchValue(newInputValue);
					}
				}}
				onOpen={() => {
					setShowDropdown(true);
				}}
			/>
		</div>
	) : (
		<></>
	);
};
